// Calendar — the messenger's own calendar surface (dock → Calendar), Google-Calendar-style.
// Month / Week / Day views, prev/today/next nav, a synced-calendars legend (toggle sources),
// app-native events (full CRUD) + read-only events pulled live from a connected Google /
// Microsoft calendar. Create/edit modal invites channel members + external emails and can
// mirror to the external calendar. Self-contained; themed via CSS vars + the .fx-modal class.
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { api, CalendarEvent, CalAttendee, PublicUser, Channel } from './api';
import { activeTz } from './prefs';
import { DateTimePicker } from './DateTimePicker';
import { Select } from './Select';

const SRC_META: Record<string, { label: string; color: string }> = {
  local: { label: 'FxM', color: '#f7b500' },
  google: { label: 'Google', color: '#4285f4' },
  microsoft: { label: 'Microsoft', color: '#2b88d8' },
};
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOUR_H = 44; // px per hour in the week/day time grid

function pad(n: number) { return String(n).padStart(2, '0'); }
function startOfDay(d: Date) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(d: Date, n: number) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfWeek(d: Date) { const x = startOfDay(d); return addDays(x, -x.getDay()); }
function sameYMD(a: Date, b: Date) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function toLocalInput(d: Date): string { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; }
function fromLocalInput(v: string): string | null { if (!v) return null; const d = new Date(v); return Number.isNaN(d.getTime()) ? null : d.toISOString(); }
function fmtTime(d: Date): string { return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: activeTz() }); }

const chipBtn: CSSProperties = { border: '1px solid var(--fx-hair)', background: 'transparent', color: 'var(--fx-text)', borderRadius: 8, padding: '6px 11px', fontSize: 13, cursor: 'pointer', font: 'inherit' };
const primaryBtn: CSSProperties = { ...chipBtn, background: 'var(--fx-accent)', color: '#1b1300', border: 'none', fontWeight: 700 };
const iconBtn: CSSProperties = { ...chipBtn, padding: '6px 9px' };
const field: CSSProperties = { width: '100%', boxSizing: 'border-box', border: '1px solid var(--fx-hair)', borderRadius: 8, background: 'var(--fx-menu-bg)', color: 'var(--fx-text)', font: 'inherit', fontSize: 13.5, padding: '8px 10px' };
const label: CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--fx-muted)', margin: '10px 0 5px', display: 'block' };

type ViewMode = 'month' | 'week' | 'day';

export function CalendarView({
  workspaceId, members, channels, meId,
}: {
  workspaceId: string | null;
  members: PublicUser[];
  channels: Channel[];
  meId?: string;
}) {
  const [mode, setMode] = useState<ViewMode>('month');
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [connected, setConnected] = useState<string[]>([]);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<CalendarEvent | { newStart: Date } | null>(null);
  const [viewing, setViewing] = useState<CalendarEvent | null>(null);

  const win = useMemo(() => {
    if (mode === 'month') {
      const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const gs = startOfWeek(first);
      return { start: gs, end: addDays(gs, 42) };
    }
    if (mode === 'week') { const ws = startOfWeek(anchor); return { start: ws, end: addDays(ws, 7) }; }
    const d0 = startOfDay(anchor); return { start: d0, end: addDays(d0, 1) };
  }, [mode, anchor]);

  const load = () => {
    api.listCalendarEvents({ start: win.start.toISOString(), end: win.end.toISOString() })
      .then((r) => { setEvents(r.events); setConnected(r.connected); }).catch(() => {});
  };
  useEffect(load, [win.start.getTime(), win.end.getTime()]);

  const connect = async (provider: 'google' | 'microsoft') => {
    try { const r = await api.calendarConnect(provider); window.location.href = r.authorize_url; } catch { /* not configured */ }
  };

  const shown = useMemo(() => events.filter((e) => !hidden.has(e.source)), [events, hidden]);
  const toggleSrc = (s: string) => setHidden((cur) => { const n = new Set(cur); n.has(s) ? n.delete(s) : n.add(s); return n; });

  const nav = (dir: number) => setAnchor((a) =>
    mode === 'month' ? startOfDay(new Date(a.getFullYear(), a.getMonth() + dir, Math.min(a.getDate(), 28)))
      : addDays(a, dir * (mode === 'week' ? 7 : 1)));

  const periodLabel = useMemo(() => {
    if (mode === 'month') return anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    if (mode === 'day') return anchor.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
    const ws = startOfWeek(anchor); const we = addDays(ws, 6);
    return `${ws.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${we.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }, [mode, anchor]);

  const sources = ['local', ...connected];

  return (
    <div className="fx-chat" style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 20px 10px', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 750, color: 'var(--fx-text)' }}>Calendar</h1>
        <button type="button" style={iconBtn} title="Previous" onClick={() => nav(-1)}>‹</button>
        <button type="button" style={chipBtn} onClick={() => setAnchor(startOfDay(new Date()))}>Today</button>
        <button type="button" style={iconBtn} title="Next" onClick={() => nav(1)}>›</button>
        <span style={{ fontSize: 15, fontWeight: 650, color: 'var(--fx-text)', minWidth: 160 }}>{periodLabel}</span>
        <span style={{ flex: 1 }} />
        <div style={{ display: 'inline-flex', border: '1px solid var(--fx-hair)', borderRadius: 8, overflow: 'hidden' }}>
          {(['month', 'week', 'day'] as ViewMode[]).map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)}
              style={{ border: 'none', padding: '6px 12px', fontSize: 13, cursor: 'pointer', textTransform: 'capitalize',
                background: mode === m ? 'var(--fx-accent)' : 'transparent', color: mode === m ? '#1b1300' : 'var(--fx-text)', fontWeight: mode === m ? 700 : 500 }}>
              {m}
            </button>
          ))}
        </div>
        <button type="button" style={primaryBtn} onClick={() => setEditing({ newStart: startOfDay(anchor) })}>+ New event</button>
      </header>

      {/* Synced-calendars legend + connect */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '0 20px 10px', flexWrap: 'wrap', fontSize: 12.5 }}>
        <span style={{ color: 'var(--fx-muted)', fontWeight: 600 }}>Calendars:</span>
        {sources.map((s) => (
          <label key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: 'var(--fx-text)' }}>
            <input type="checkbox" checked={!hidden.has(s)} onChange={() => toggleSrc(s)} />
            <span style={{ width: 10, height: 10, borderRadius: 3, background: SRC_META[s]?.color || '#888', display: 'inline-block' }} />
            {SRC_META[s]?.label || s}
          </label>
        ))}
        {(['google', 'microsoft'] as const).filter((p) => !connected.includes(p)).map((p) => (
          <button key={p} type="button" style={{ ...chipBtn, padding: '3px 10px', fontSize: 12 }} onClick={() => connect(p)}>Connect {SRC_META[p].label}</button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', minHeight: 0, padding: '0 16px 20px' }}>
        {mode === 'month'
          ? <MonthGrid anchor={anchor} events={shown} onDay={(d) => setEditing({ newStart: new Date(d.getFullYear(), d.getMonth(), d.getDate(), 9, 0) })} onEvent={setViewing} onExternal={setViewing} />
          : <TimeGrid days={mode === 'week' ? startOfWeek(anchor) : startOfDay(anchor)} count={mode === 'week' ? 7 : 1} events={shown} onSlot={(d) => setEditing({ newStart: d })} onEvent={setViewing} onExternal={setViewing} />}
      </div>

      {editing && (
        <EventModal
          event={'id' in editing ? editing : null}
          defaultStart={'newStart' in editing ? editing.newStart : undefined}
          members={members} channels={channels} meId={meId}
          workspaceId={workspaceId} connected={connected}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {viewing && (
        <EventDetail
          event={viewing}
          members={members}
          meId={meId}
          onClose={() => setViewing(null)}
          onEdit={() => { setEditing(viewing); setViewing(null); }}
          onDeleted={() => { setViewing(null); load(); }}
        />
      )}
    </div>
  );
}

// ── Read-only event detail (opens on click; the whole event — when, guests, agenda,
//    location, meeting link — laid out like our editor form; join in-app, don't redirect) ──
const RESP_META: Record<string, { label: string; color: string; mark: string }> = {
  accepted: { label: 'Going', color: '#2ea36b', mark: '✓' },
  declined: { label: 'Declined', color: 'var(--fx-alert,#e56a6a)', mark: '✗' },
  tentative: { label: 'Maybe', color: '#d99a4e', mark: '?' },
  pending: { label: 'Invited', color: 'var(--fx-muted)', mark: '•' },
};
const RECUR_LABEL: Record<string, string> = {
  daily: 'Repeats every day', weekdays: 'Repeats every weekday (Mon–Fri)',
  weekly: 'Repeats every week', monthly: 'Repeats every month',
};
function reminderLabel(m?: number | null): string | null {
  if (!m) return null;
  if (m === 60) return 'Reminder 1 hour before';
  if (m % 60 === 0) return `Reminder ${m / 60} hours before`;
  return `Reminder ${m} minutes before`;
}
function initials(s: string): string {
  const p = s.trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase() || '?';
}

function EventDetail({ event, members, meId, onClose, onEdit, onDeleted }: {
  event: CalendarEvent;
  members: PublicUser[];
  meId?: string;
  onClose: () => void;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const start = event.start ? new Date(event.start) : null;
  const end = event.end ? new Date(event.end) : null;
  const locIsUrl = !!event.location && /^https?:\/\//.test(event.location);
  const join = event.meeting_link || (locIsUrl ? event.location : null);
  const provider = event.external_provider ? (SRC_META[event.external_provider]?.label || event.external_provider) : null;
  const del = async () => {
    if (!window.confirm('Delete this event?')) return;
    setBusy(true);
    try { await api.deleteCalendarEvent(event.id); onDeleted(); } catch { setBusy(false); }
  };

  // Unified guest list: external events arrive pre-resolved (with RSVP); local events are
  // resolved here from their user/email attendees via the workspace member list.
  const memberOf = (id: string) => members.find((m) => m.id === id);
  const guests = (event.guests && event.guests.length)
    ? event.guests.map((g) => ({ name: g.name || g.email || 'Guest', email: g.email || '', response: g.response || null, organizer: !!g.organizer }))
    : (event.attendees || []).map((a) => {
        if (a.type === 'user') { const m = memberOf(a.id); return { name: m?.name || m?.email || 'Teammate', email: m?.email || '', response: null, organizer: a.id === meId }; }
        return { name: a.email, email: a.email, response: null, organizer: false };
      });

  const when = event.all_day
    ? (start ? `${start.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} · All day` : '')
    : `${start ? start.toLocaleString([], { weekday: 'long', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''} – ${end ? end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}`;
  const recurLabel = event.recurrence ? (RECUR_LABEL[event.recurrence] || 'Repeats') : null;
  const remindLabel = reminderLabel(event.reminder_minutes);

  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal" onClick={(e) => e.stopPropagation()} style={{ width: 'min(520px, 94vw)', maxHeight: '88vh', overflowY: 'auto' }}>
        <div className="fx-login-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: SRC_META[event.source]?.color || 'var(--fx-accent)', flex: 'none' }} />
          <span>{event.is_call ? '📞 ' : ''}{event.title}</span>
        </div>

        {/* When */}
        <label style={label}>When</label>
        <div style={{ fontSize: 14, color: 'var(--fx-text)' }}>{when}</div>
        {(recurLabel || remindLabel) && (
          <div style={{ fontSize: 12.5, color: 'var(--fx-muted)', marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {recurLabel && <span>🔁 {recurLabel}</span>}
            {remindLabel && <span>🔔 {remindLabel}</span>}
          </div>
        )}

        {/* Meeting link / location */}
        {join && (
          <>
            <label style={label}>{event.is_call ? 'Call link' : 'Meeting link'}</label>
            <a href={join} target="_blank" rel="noreferrer" style={{ fontSize: 13.5, color: 'var(--fx-accent)', wordBreak: 'break-all' }}>{join}</a>
          </>
        )}
        {event.location && !locIsUrl && (
          <>
            <label style={label}>Location</label>
            <div style={{ fontSize: 14, color: 'var(--fx-text)' }}><span style={{ color: 'var(--fx-muted)' }}>📍 </span>{event.location}</div>
          </>
        )}

        {/* Agenda / description */}
        {event.description && (
          <>
            <label style={label}>Agenda</label>
            <div style={{ fontSize: 13.5, whiteSpace: 'pre-wrap', color: 'var(--fx-text)', lineHeight: 1.5, background: 'var(--fx-menu-bg)', border: '1px solid var(--fx-hair)', borderRadius: 8, padding: '9px 11px', maxHeight: 200, overflowY: 'auto' }}>{event.description}</div>
          </>
        )}

        {/* Organizer */}
        {event.organizer && (
          <>
            <label style={label}>Organizer</label>
            <div style={{ fontSize: 13.5, color: 'var(--fx-text)' }}>{event.organizer}</div>
          </>
        )}

        {/* Guests */}
        {guests.length > 0 && (
          <>
            <label style={label}>Guests · {guests.length}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
              {guests.map((g, i) => {
                const rm = g.response ? RESP_META[g.response] : null;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '5px 2px' }}>
                    <span style={{ width: 26, height: 26, borderRadius: '50%', flex: 'none', background: 'color-mix(in srgb, var(--fx-accent) 22%, transparent)', color: 'var(--fx-text)', fontSize: 11, fontWeight: 700, display: 'grid', placeItems: 'center' }}>{initials(g.name)}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 13.5, color: 'var(--fx-text)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {g.name}{g.organizer && <span style={{ fontSize: 11, color: 'var(--fx-muted)', fontWeight: 600 }}> · Organizer</span>}
                      </span>
                      {g.email && g.email !== g.name && <span style={{ fontSize: 11.5, color: 'var(--fx-muted)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.email}</span>}
                    </span>
                    {rm && <span style={{ fontSize: 11.5, fontWeight: 650, color: rm.color, flex: 'none' }}>{rm.mark} {rm.label}</span>}
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="fx-modal-actions" style={{ marginTop: 18, flexWrap: 'wrap', gap: 8 }}>
          {join && (
            <a href={join} target="_blank" rel="noreferrer" style={{ ...primaryBtn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
              {event.is_call ? '📞 Join call' : '🎥 Join meeting'}
            </a>
          )}
          {event.external_link && (
            <a href={event.external_link} target="_blank" rel="noreferrer" style={{ ...chipBtn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>Open in {provider || 'calendar'}</a>
          )}
          {event.editable && <button type="button" style={chipBtn} onClick={onEdit}>Edit</button>}
          {event.editable && <button type="button" style={{ ...chipBtn, color: 'var(--fx-alert)', borderColor: 'var(--fx-alert)' }} disabled={busy} onClick={del}>Delete</button>}
          <button type="button" style={chipBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Month grid ────────────────────────────────────────────────────────────────
function MonthGrid({ anchor, events, onDay, onEvent, onExternal }: {
  anchor: Date; events: CalendarEvent[];
  onDay: (d: Date) => void; onEvent: (e: CalendarEvent) => void; onExternal: (e: CalendarEvent) => void;
}) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gs = startOfWeek(first);
  const days = Array.from({ length: 42 }, (_, i) => addDays(gs, i));
  const today = new Date();
  const byDay = (d: Date) => events.filter((e) => e.start && sameYMD(new Date(e.start), d)).sort((a, b) => (a.start || '').localeCompare(b.start || ''));

  return (
    <div className="fx-cal-month">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {WD.map((w) => (
          <div key={w} className="fx-cal-wd">{w}</div>
        ))}
        {days.map((d, i) => {
          const inMonth = d.getMonth() === anchor.getMonth();
          const isToday = sameYMD(d, today);
          const evs = byDay(d);
          return (
            <div key={i} onClick={() => onDay(d)}
              className={`fx-cal-cell${inMonth ? '' : ' is-out'}`}
              style={{ borderRight: (i % 7 !== 6) ? '1px solid var(--fx-hair)' : 'none',
                borderBottom: i < 35 ? '1px solid var(--fx-hair)' : 'none' }}>
              <div className="fx-cal-daynum">
                <span style={{ display: 'inline-block', minWidth: 20, padding: '1px 5px', borderRadius: 99, fontWeight: isToday ? 700 : 500,
                  background: isToday ? 'var(--fx-accent)' : 'transparent', color: isToday ? '#1b1300' : (inMonth ? 'var(--fx-text)' : 'var(--fx-faint)') }}>{d.getDate()}</span>
              </div>
              {evs.slice(0, 3).map((e) => {
                const c = SRC_META[e.source]?.color || '#888';
                return (
                  <div key={e.id} onClick={(ev) => { ev.stopPropagation(); e.editable ? onEvent(e) : onExternal(e); }}
                    title={e.title} className="fx-cal-ev"
                    style={{ background: `color-mix(in srgb, ${c} 16%, transparent)` }}>
                    <span className="fx-cal-ev-dot" style={{ background: c }} />
                    <span className="fx-cal-ev-t">{e.is_call ? '📞 ' : ''}{!e.all_day && e.start ? fmtTime(new Date(e.start)) + ' ' : ''}{e.title}</span>
                  </div>
                );
              })}
              {evs.length > 3 && <div className="fx-cal-more">+{evs.length - 3} more</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Week / Day time grid ────────────────────────────────────────────────────────
function TimeGrid({ days, count, events, onSlot, onEvent, onExternal }: {
  days: Date; count: number; events: CalendarEvent[];
  onSlot: (d: Date) => void; onEvent: (e: CalendarEvent) => void; onExternal: (e: CalendarEvent) => void;
}) {
  const cols = Array.from({ length: count }, (_, i) => addDays(days, i));
  const today = new Date();
  const hours = Array.from({ length: 24 }, (_, h) => h);
  const timed = (d: Date) => events.filter((e) => e.start && !e.all_day && sameYMD(new Date(e.start), d));
  const allDay = (d: Date) => events.filter((e) => e.start && e.all_day && sameYMD(new Date(e.start), d));

  return (
    <div className="fx-cal-tg" style={{ ['--cal-cols' as any]: count }}>
      {/* header row */}
      <div className="fx-cal-tg-grid" style={{ borderBottom: '1px solid var(--fx-hair)' }}>
        <div />
        {cols.map((d, i) => (
          <div key={i} style={{ padding: '7px 8px', textAlign: 'center', borderLeft: '1px solid var(--fx-hair)' }}>
            <div style={{ fontSize: 11.5, color: 'var(--fx-muted)', fontWeight: 600 }}>{WD[d.getDay()]}</div>
            <div style={{ fontSize: 15, fontWeight: sameYMD(d, today) ? 700 : 500, color: sameYMD(d, today) ? 'var(--fx-accent)' : 'var(--fx-text)' }}>{d.getDate()}</div>
            {allDay(d).map((e) => {
              const c = SRC_META[e.source]?.color || '#888';
              return <div key={e.id} onClick={() => (e.editable ? onEvent(e) : onExternal(e))} style={{ marginTop: 2, fontSize: 11, padding: '1px 5px', borderRadius: 4, background: `color-mix(in srgb, ${c} 18%, transparent)`, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.title}</div>;
            })}
          </div>
        ))}
      </div>
      {/* scrollable hour grid */}
      <div className="fx-cal-tg-grid" style={{ position: 'relative' }}>
        <div>
          {hours.map((h) => (
            <div key={h} style={{ height: HOUR_H, fontSize: 10.5, color: 'var(--fx-faint)', textAlign: 'right', paddingRight: 6, transform: 'translateY(-6px)' }}>
              {h === 0 ? '' : `${(h % 12) || 12} ${h < 12 ? 'AM' : 'PM'}`}
            </div>
          ))}
        </div>
        {cols.map((d, ci) => (
          <div key={ci} style={{ position: 'relative', borderLeft: '1px solid var(--fx-hair)' }}>
            {hours.map((h) => (
              <div key={h} onClick={() => onSlot(new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, 0))}
                style={{ height: HOUR_H, borderTop: '1px solid color-mix(in srgb, var(--fx-hair) 60%, transparent)', cursor: 'pointer' }} />
            ))}
            {timed(d).map((e) => {
              const s = new Date(e.start!); const en = e.end ? new Date(e.end) : new Date(s.getTime() + 30 * 60000);
              const top = (s.getHours() + s.getMinutes() / 60) * HOUR_H;
              const height = Math.max(18, ((en.getTime() - s.getTime()) / 3600000) * HOUR_H - 2);
              const c = SRC_META[e.source]?.color || '#888';
              return (
                <div key={e.id} onClick={() => (e.editable ? onEvent(e) : onExternal(e))} title={e.title}
                  style={{ position: 'absolute', top, height, left: 3, right: 3, borderRadius: 6, padding: '2px 6px', overflow: 'hidden',
                    background: `color-mix(in srgb, ${c} 22%, var(--fx-menu-bg))`, borderLeft: `3px solid ${c}`, cursor: 'pointer', fontSize: 11.5, color: 'var(--fx-text)' }}>
                  <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.is_call ? '📞 ' : ''}{e.title}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--fx-muted)' }}>{fmtTime(s)}</div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Create / edit modal ─────────────────────────────────────────────────────────
function EventModal({
  event, defaultStart, members, meId, workspaceId, connected, onClose, onSaved,
}: {
  event: CalendarEvent | null;
  defaultStart?: Date;
  members: PublicUser[];
  channels: Channel[];
  meId?: string;
  workspaceId: string | null;
  connected: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const base = defaultStart || new Date(Math.ceil(Date.now() / 1800000) * 1800000);
  const [title, setTitle] = useState(event?.title || '');
  const [start, setStart] = useState(event?.start ? toLocalInput(new Date(event.start)) : toLocalInput(base));
  const [end, setEnd] = useState(event?.end ? toLocalInput(new Date(event.end)) : toLocalInput(new Date(base.getTime() + 30 * 60000)));
  const [allDay, setAllDay] = useState(!!event?.all_day);
  const [isCall, setIsCall] = useState(!!event?.is_call);
  const [description, setDescription] = useState(event?.description || '');
  const [location, setLocation] = useState(event?.location || '');
  const [recurrence, setRecurrence] = useState<string>(event?.recurrence || '');
  const [reminder, setReminder] = useState<string>(event?.reminder_minutes != null ? String(event.reminder_minutes) : '');
  const [userIds, setUserIds] = useState<string[]>((event?.attendees || []).filter((a): a is { type: 'user'; id: string } => a.type === 'user').map((a) => a.id));
  const [emails, setEmails] = useState<string[]>((event?.attendees || []).filter((a): a is { type: 'email'; email: string } => a.type === 'email').map((a) => a.email));
  const [emailDraft, setEmailDraft] = useState('');
  const [syncExternal, setSyncExternal] = useState(connected.length > 0 && !event);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const toggleUser = (id: string) => setUserIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const addEmail = () => { const e = emailDraft.trim(); if (e && /.+@.+\..+/.test(e) && !emails.includes(e)) setEmails((cur) => [...cur, e]); setEmailDraft(''); };

  const save = async () => {
    if (!title.trim()) { setErr('Give the event a title.'); return; }
    const s = fromLocalInput(start); const en = fromLocalInput(end);
    if (!s || !en || new Date(en) < new Date(s)) { setErr('Check the start and end times.'); return; }
    setBusy(true); setErr('');
    const attendees: CalAttendee[] = [...userIds.map((id) => ({ type: 'user' as const, id })), ...emails.map((email) => ({ type: 'email' as const, email }))];
    const body = { title: title.trim(), start: s, end: en, all_day: allDay, is_call: isCall, description: description.trim() || undefined, location: location.trim() || undefined, attendees, workspace_id: workspaceId || undefined, sync_external: syncExternal && connected.length > 0, provider: connected[0], recurrence: recurrence || undefined, reminder_minutes: reminder ? Number(reminder) : undefined };
    try { if (event) await api.updateCalendarEvent(event.id, body); else await api.createCalendarEvent(body); onSaved(); }
    catch (e: any) { setErr(e?.message || 'Could not save the event.'); setBusy(false); }
  };
  const del = async () => { if (!event || !confirm('Delete this event?')) return; setBusy(true); try { await api.deleteCalendarEvent(event.id); onSaved(); } catch (e: any) { setErr(e?.message || 'Could not delete.'); setBusy(false); } };
  const others = members.filter((m) => m.id !== meId);

  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal" onClick={(e) => e.stopPropagation()} style={{ width: 'min(540px, 94vw)', maxHeight: '88vh', overflowY: 'auto' }}>
        <div className="fx-login-title">{event ? 'Edit event' : 'New event'}</div>
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Event title" style={{ ...field, fontSize: 15, marginTop: 4 }} />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 180 }}><label style={label}>Starts</label><DateTimePicker value={start} onChange={setStart} mode="datetime" style={{ width: '100%' }} /></div>
          <div style={{ flex: 1, minWidth: 180 }}><label style={label}>Ends</label><DateTimePicker value={end} onChange={setEnd} mode="datetime" style={{ width: '100%' }} /></div>
        </div>
        <div style={{ display: 'flex', gap: 18, marginTop: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--fx-text)', cursor: 'pointer' }}><input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} /> All day</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--fx-text)', cursor: 'pointer' }}><input type="checkbox" checked={isCall} onChange={(e) => setIsCall(e.target.checked)} /> 📞 This is a call</label>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 150 }}>
            <label style={label}>Repeat</label>
            <Select value={recurrence} onChange={setRecurrence} placeholder="Does not repeat" style={{ width: '100%' }}
              options={[
                { value: '', label: 'Does not repeat' },
                { value: 'daily', label: 'Every day' },
                { value: 'weekdays', label: 'Every weekday (Mon–Fri)' },
                { value: 'weekly', label: 'Every week' },
                { value: 'monthly', label: 'Every month' },
              ]} />
          </div>
          <div style={{ flex: 1, minWidth: 150 }}>
            <label style={label}>Remind me</label>
            <Select value={reminder} onChange={setReminder} placeholder="No reminder" style={{ width: '100%' }}
              options={[
                { value: '', label: 'No reminder' },
                { value: '5', label: '5 minutes before' },
                { value: '10', label: '10 minutes before' },
                { value: '30', label: '30 minutes before' },
                { value: '60', label: '1 hour before' },
              ]} />
          </div>
        </div>
        <label style={label}>Guests</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 108, overflowY: 'auto', marginBottom: 6 }}>
          {others.map((m) => (
            <button key={m.id} type="button" onClick={() => toggleUser(m.id)} style={{ ...chipBtn, padding: '4px 9px', fontSize: 12.5, background: userIds.includes(m.id) ? 'var(--fx-accent)' : 'transparent', color: userIds.includes(m.id) ? '#1b1300' : 'var(--fx-text)', borderColor: userIds.includes(m.id) ? 'var(--fx-accent)' : 'var(--fx-hair)' }}>{m.name || m.email}</button>
          ))}
          {others.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--fx-faint)' }}>No teammates to invite.</span>}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input value={emailDraft} onChange={(e) => setEmailDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addEmail(); } }} placeholder="Invite external email…" style={{ ...field, flex: 1 }} />
          <button type="button" style={chipBtn} onClick={addEmail}>Add</button>
        </div>
        {emails.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {emails.map((e) => (<span key={e} style={{ ...chipBtn, padding: '3px 8px', fontSize: 12, display: 'inline-flex', gap: 6, alignItems: 'center' }}>{e}<span style={{ cursor: 'pointer', color: 'var(--fx-muted)' }} onClick={() => setEmails((cur) => cur.filter((x) => x !== e))}>×</span></span>))}
          </div>
        )}
        <label style={label}>Location</label>
        <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Add a place or a link" style={field} />
        <label style={label}>Description</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Notes / agenda" style={{ ...field, minHeight: 54, resize: 'vertical' }} />
        {connected.length > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--fx-text)', cursor: 'pointer', marginTop: 12 }}>
            <input type="checkbox" checked={syncExternal} onChange={(e) => setSyncExternal(e.target.checked)} disabled={!!event} />
            Add to my {SRC_META[connected[0]]?.label || connected[0]} calendar & send invites{event ? ' (new events only)' : ''}
          </label>
        )}
        {err && <div style={{ color: 'var(--fx-alert)', fontSize: 13, marginTop: 12 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          {event && <button type="button" style={{ ...chipBtn, color: 'var(--fx-alert)', borderColor: 'var(--fx-alert)', marginRight: 'auto' }} disabled={busy} onClick={del}>Delete</button>}
          <button type="button" style={chipBtn} disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" style={primaryBtn} disabled={busy} onClick={save}>{busy ? 'Saving…' : event ? 'Save' : 'Create event'}</button>
        </div>
      </div>
    </div>
  );
}
